// components/NanoGasCanvas.tsx
import React, { useEffect, useRef } from "react";

interface NanoGasCanvasProps {
  isActive: boolean;
  gasLevel: number; // 슬라이더 값(0~100)을 받아와 연출에 직접 반영합니다.
}

export const NanoGasCanvas: React.FC<NanoGasCanvasProps> = ({ isActive, gasLevel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let particles: GasParticle[] = [];
    let animationFrameId: number;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", handleMouseMove);
    resize();

    class GasParticle {
      x!: number;
      y!: number;
      size!: number;
      speedY!: number;
      speedX!: number;
      opacity!: number;
      wobbleSpeed!: number;
      wobbleScale!: number;

      constructor() {
        this.init(true);
      }

      init(isFirstLoad = false) {
        this.x = Math.random() * canvas!.width;
        // 가스가 밑에서 뿜어져 올라오는 느낌을 주기 위해 바닥 근처에서 생성
        this.y = isFirstLoad ? Math.random() * canvas!.height : canvas!.height + 100;
        
        // ✨ 가스 덩어리 느낌을 내기 위해 크기를 기존보다 15배 이상 대폭 키웁니다 (40px ~ 90px)
        this.size = Math.random() * 50 + 40; 
        
        // 위로 뿜어져 올라가는 속도
        this.speedY = -(Math.random() * 1.5 + 0.5); 
        this.speedX = Math.random() * 1.0 - 0.5;
        this.opacity = Math.random() * 0.5 + 0.3;
        
        // 가스가 일렁거리며 번지는 효과를 위한 계수
        this.wobbleSpeed = Math.random() * 0.02 + 0.01;
        this.wobbleScale = Math.random() * 20;
      }

      update() {
        this.y += this.speedY;
        // 좌우로 출렁거리며 연기가 피어오르는 움직임 계산
        this.x += this.speedX + Math.sin(this.y * this.wobbleSpeed) * 0.3;

        // 마우스 커서 기류 반응 (가스 덩어리가 마우스를 스르륵 밀어내거나 감싸는 효과)
        const dx = mouse.current.x - this.x;
        const dy = mouse.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 250) {
          this.x -= (dx / distance) * 1.2;
        }

        // 화면 위로 완전히 증발하면 다시 바닥에서 리스폰
        if (this.y < -this.size || this.x > canvas!.width + this.size || this.x < -this.size) {
          this.init(false);
        }
      }

      draw() {
        if (!ctx) return;
        // 💡 뭉치는 효과를 극대화하기 위해 알파 채널이 들어간 완전한 구체를 그립니다.
        ctx.fillStyle = `rgba(52, 211, 153, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 슬라이더 농도(gasLevel)에 따라 화면에 존재하는 가스 덩어리 수를 유동적으로 조절
    // 뭉치는 효과 특성상 너무 많으면 화면이 꽉 차므로 최대 70개 내외가 가장 예쁩니다.
    const particleCount = Math.max(5, Math.floor(gasLevel * 0.7));
    particles = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(new GasParticle());
    }

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✨ [핵심 기믹] 가스들이 서로 들러붙게 만드는 매직 필터 설정
      // 가스 입자들을 의도적으로 흐리게(blur) 만든 뒤, contrast를 30배(3000%) 튀겨서 
      // 경계선이 만나는 지점이 끈적하게 달라붙도록 캔버스 자체에 필터를 먹입니다.
      if (isActive && gasLevel > 5) {
        ctx.filter = "blur(30px) contrast(30)";
      } else {
        ctx.filter = "blur(8px) contrast(1.2)"; // 평소에는 아주 미세한 연기 무드
      }

      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      // 다음 레이어에 영향을 주지 않도록 필터 초기화
      ctx.filter = "none";

      animationFrameId = requestAnimationFrame(render);
    };
    render();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isActive, gasLevel]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 z-0 pointer-events-none transition-opacity duration-1000 ${
        isActive ? "opacity-40" : "opacity-10"
      }`}
      style={{
        // 캔버스 자체 픽셀이 contrast(30) 때문에 타버리는 것을 방지하기 위한 웹킷 필터 보정
        mixBlendMode: "screen" 
      }}
    />
  );
};